import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function TabImport({ project, onChange }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [opts, setOpts] = useState(project.import_options || {});
  const [templates, setTemplates] = useState(null);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(project.applied_template_id || "");
  const [showSave, setShowSave] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveDesc, setSaveDesc] = useState("");
  const [gdocUrl, setGdocUrl] = useState("");
  const [gdocBusy, setGdocBusy] = useState(false);

  const loadCustom = useCallback(async () => {
    try {
      const { data } = await api.get("/ph360/custom-templates");
      setCustomTemplates(data || []);
    } catch (err) {
      // Non-critical — user just won't see any custom templates listed.
      // Surface non-404 errors to the console for debugging (silent for 404 which is expected on first-time users).
      if (err?.response?.status !== 404) console.warn("loadCustom failed:", err?.message || err);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/ph360/starter-templates");
        setTemplates(data);
      } catch (err) {
        console.warn("loadStarterTemplates failed:", err?.message || err);
      }
    })();
    loadCustom();
  }, [loadCustom]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a file first"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(opts).forEach(([k,v]) => fd.append(k, v ? "true" : "false"));
    try {
      const { data } = await api.post("/ph360/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data);
      toast.success(`Imported ${data.structured.chapters.length} chapter(s)`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const clearProject = async () => {
    if (!window.confirm("Clear the current PH360 project? Saved presets will be kept.")) return;
    try {
      const { data } = await api.post("/ph360/project/clear");
      onChange(data);
      toast.success("Project cleared");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const applyTemplate = async (id) => {
    setSelectedTemplate(id);
    try {
      const { data } = await api.post("/ph360/starter-templates/apply", { template_id: id });
      onChange(data);
      toast.success(`Applied "${templates[id].label}" template`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveAsCustom = async (e) => {
    e?.preventDefault();
    if (!saveName.trim()) { toast.error("Name required"); return; }
    try {
      await api.post("/ph360/custom-templates", { name: saveName.trim(), description: saveDesc.trim() });
      toast.success("Saved as custom template");
      setShowSave(false); setSaveName(""); setSaveDesc("");
      await loadCustom();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const applyCustom = async (id) => {
    try {
      const { data } = await api.post(`/ph360/custom-templates/${id}/apply`);
      onChange(data);
      toast.success("Custom template applied");
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const deleteCustom = async (id) => {
    if (!window.confirm("Delete this custom template?")) return;
    try {
      await api.delete(`/ph360/custom-templates/${id}`);
      await loadCustom();
      toast.success("Deleted");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <>
      <h2>Import Content</h2>
      <p>Upload TXT, DOCX, or PDF. For already-formatted PDFs, keep cleanup options checked.</p>

      {/* Starter Templates Gallery */}
      {templates && (
        <>
          <h3>Or start from a preset ebook template</h3>
          <p className="description">One-click pre-fill for layout, front matter, back matter, and theme pack. Applies to the current project — you can still edit anything afterward.</p>
          <div className="ef360-template-gallery" data-testid="template-gallery">
            {Object.entries(templates).map(([id, t]) => (
              <button
                key={id}
                className={`ef360-template-card ${selectedTemplate === id ? "selected" : ""}`}
                onClick={()=>applyTemplate(id)}
                data-testid={`template-card-${id}`}
              >
                <div className="ef360-template-card-title">{t.label}</div>
                <div className="ef360-template-card-desc">{t.description}</div>
                {selectedTemplate === id && <div className="ef360-template-card-badge">✓ Applied</div>}
              </button>
            ))}
          </div>
          <hr/>
        </>
      )}

      {/* Custom templates */}
      <h3>Your Custom Templates</h3>
      <p className="description">Save the current project's layout + matter as a reusable template you can apply to future books.</p>
      <div className="ef360-actions">
        <button className="button" onClick={()=>setShowSave(true)} data-testid="custom-tpl-save-btn">💾 Save current as custom template</button>
      </div>
      {showSave && (
        <form onSubmit={saveAsCustom} className="ef360-inline-form" data-testid="custom-tpl-save-form">
          <label>Template name</label>
          <input type="text" value={saveName} onChange={(e)=>setSaveName(e.target.value)} data-testid="custom-tpl-name" autoFocus/>
          <label>Description (optional)</label>
          <input type="text" value={saveDesc} onChange={(e)=>setSaveDesc(e.target.value)} data-testid="custom-tpl-desc"/>
          <p style={{marginTop:10}}>
            <button type="submit" className="button button-primary" data-testid="custom-tpl-save-submit">Save</button>{" "}
            <button type="button" className="button" onClick={()=>setShowSave(false)}>Cancel</button>
          </p>
        </form>
      )}
      {customTemplates.length > 0 && (
        <div className="ef360-template-gallery" data-testid="custom-tpl-gallery">
          {customTemplates.map(t => (
            <div key={t.id} className="ef360-template-card" data-testid={`custom-tpl-card-${t.id}`}>
              <div className="ef360-template-card-title">{t.name}</div>
              <div className="ef360-template-card-desc">{t.description || `Theme: ${t.layout_theme_pack || "custom"}`}</div>
              <div style={{display:"flex", gap:6, marginTop:8}}>
                <button className="button" onClick={()=>applyCustom(t.id)} data-testid={`custom-tpl-apply-${t.id}`}>Apply</button>
                <button className="button delete" onClick={()=>deleteCustom(t.id)} data-testid={`custom-tpl-delete-${t.id}`}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <hr/>

      <h3>Upload a manuscript</h3>
      <div className="notice notice-info">
        <p><strong>Current import engine v3.0.0-alpha.2:</strong> Import starts a fresh project, resets the previous cover image, removes imported TOCs without deleting the real manuscript, restores all numbered chapters, and recovers metadata.</p>
      </div>
      <form onSubmit={submit}>
        {[
          ["remove_imported_frontmatter","Remove existing imported front matter/copyright/disclaimer from PDF"],
          ["remove_imported_headers_footers","Remove imported headers/footers/page numbers"],
          ["remove_imported_toc","Remove imported table of contents"],
          ["preserve_imported_images","Keep imported manuscript images (DOCX only)"],
        ].map(([k,label]) => (
          <label className="checkbox-line" key={k}>
            <input type="checkbox" checked={!!opts[k]} onChange={(e)=>setOpts({...opts,[k]:e.target.checked})} data-testid={`opt-${k}`}/>
            {label}
          </label>
        ))}
        <p className="description">When enabled, embedded DOCX images are saved and rendered at a uniform chapter-width size.</p>
        <p><input type="file" accept=".txt,.docx,.pdf,.md" required onChange={(e)=>setFile(e.target.files?.[0]||null)} data-testid="ph360-import-file"/></p>
        <button type="submit" className="button button-primary" disabled={busy} data-testid="ph360-import-submit">
          {busy ? "Importing…" : "Import + Clean + Structure"}
        </button>
      </form>

      <hr/>
      <h3>Import from Google Docs URL</h3>
      <p className="description">
        Paste a Google Docs link that's shared as <strong>Anyone with the link — Viewer</strong>.
        We fetch it as a DOCX and run it through the same importer.
      </p>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (!gdocUrl.trim()) { toast.error("Paste a Google Docs URL"); return; }
          setGdocBusy(true);
          try {
            const { data } = await api.post("/ph360/import/gdoc", {
              url: gdocUrl.trim(),
              ...opts,
            });
            onChange(data);
            toast.success(`Imported ${data.structured.chapters.length} chapter(s) from Google Docs`);
            setGdocUrl("");
          } catch (err) { toast.error(formatApiError(err)); }
          finally { setGdocBusy(false); }
        }}
        data-testid="gdoc-import-form"
        style={{display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", marginTop:8}}
      >
        <input
          type="url"
          value={gdocUrl}
          onChange={(e)=>setGdocUrl(e.target.value)}
          placeholder="https://docs.google.com/document/d/…/edit"
          data-testid="gdoc-url-input"
          style={{flex:"1 1 380px", minWidth:280}}
        />
        <button type="submit" className="button button-primary" disabled={gdocBusy} data-testid="gdoc-import-submit">
          {gdocBusy ? "Fetching…" : "Import from Google Docs"}
        </button>
      </form>

      <hr/>
      <button className="button delete" onClick={clearProject} data-testid="ph360-clear-project">Clear Current Project</button>
    </>
  );
}
